// GENERATED by kanban-board-be `npm run contract:export`. Do not edit.
// Source of truth: kanban-board-be/src/contract. Change it there and re-export.

/**
 * Fractional indexing — ordering cards without renumbering them.
 *
 * THIS IS THE REASON A DRAG IS ONE ROW WRITE.
 *
 * The obvious way to order a column is an integer `position` per card. Dropping
 * a card between positions 3 and 4 then means rewriting every card below it,
 * which is a write amplification of O(column length) on the single most common
 * interaction in the product, and — far worse for us — O(column length) rows
 * bumped to a new `version`, which the sync engine then has to push to every
 * connected client. One drag would broadcast the whole column.
 *
 * Instead a position is a *string* that sorts lexicographically, and there is
 * always room to generate another string between any two others. A drag writes
 * exactly one row and broadcasts exactly one delta, whether the column holds
 * ten cards or ten thousand.
 *
 *   a0            first card
 *   a0  a1        append
 *   a0  a0V  a1   insert between: only the new card is written
 *
 * The implementation is the well-known base62 order-key algorithm (Greenspan,
 * "Implementing Fractional Indexing"; the same construction Figma and Rocicorp
 * use). It is reproduced here rather than taken as a dependency because it is
 * small, it must never change behaviour under us, and the client repo needs the
 * identical function — a client and server that disagreed about midpoints would
 * interleave cards differently in the same column.
 *
 * A key is an integer part and a fractional part. The integer part's first
 * character encodes its own length, so keys of different magnitudes still
 * compare correctly as plain strings.
 *
 * Keys grow by roughly one character per repeated insert into the *same* gap.
 * That is fine: inserting at one spot 50 times leaves a 50-character key and
 * nothing breaks. If it ever becomes untidy a background job can rebalance a
 * column, but at 15 users it will not come up.
 */

const BASE_62_DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

/** The key assigned to the first card in an empty column. */
export const FIRST_INDEX = "a0";

function getIntegerLength(head: string): number {
  if (head >= "a" && head <= "z") {
    return head.charCodeAt(0) - "a".charCodeAt(0) + 2;
  }
  if (head >= "A" && head <= "Z") {
    return "Z".charCodeAt(0) - head.charCodeAt(0) + 2;
  }
  throw new Error(`invalid order key head: ${head}`);
}

function getIntegerPart(key: string): string {
  const length = getIntegerLength(key.charAt(0));
  if (length > key.length) throw new Error(`invalid order key: ${key}`);
  return key.slice(0, length);
}

function validateInteger(int: string): void {
  if (int.length !== getIntegerLength(int.charAt(0))) {
    throw new Error(`invalid integer part of order key: ${int}`);
  }
}

function validateOrderKey(key: string, digits: string): void {
  if (key === "A" + digits.charAt(0).repeat(26)) {
    throw new Error(`invalid order key: ${key}`);
  }
  const int = getIntegerPart(key);
  const frac = key.slice(int.length);
  if (frac.slice(-1) === digits.charAt(0)) {
    // A trailing zero makes a key non-canonical: "a01" and "a010" would sort
    // adjacently with no room between them, and midpoint() would not terminate.
    throw new Error(`invalid order key: ${key}`);
  }
}

/**
 * A string strictly between `a` and `b`, both fractional parts.
 * An empty `a` means "the start"; a null `b` means "the end".
 */
function midpoint(a: string, b: string | null, digits: string): string {
  if (b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a.slice(-1) === digits.charAt(0) || (b !== null && b.slice(-1) === digits.charAt(0))) {
    throw new Error("trailing zero in order key");
  }

  if (b !== null) {
    // Walk past the shared prefix; the interesting work is where they diverge.
    let n = 0;
    while ((a.charAt(n) || digits.charAt(0)) === b.charAt(n)) n += 1;
    if (n > 0) {
      return b.slice(0, n) + midpoint(a.slice(n), b.slice(n), digits);
    }
  }

  const digitA = a === "" ? 0 : digits.indexOf(a.charAt(0));
  const digitB = b !== null ? digits.indexOf(b.charAt(0)) : digits.length;

  if (digitB - digitA > 1) {
    // There is a digit between them; take it and stop.
    return digits.charAt(Math.round(0.5 * (digitA + digitB)));
  }

  // The digits are adjacent, so the answer has to be longer than both.
  if (b !== null && b.length > 1) {
    return b.slice(0, 1);
  }
  return digits.charAt(digitA) + midpoint(a.slice(1), null, digits);
}

function incrementInteger(x: string, digits: string): string | null {
  validateInteger(x);
  const head = x.charAt(0);
  const digs = x.slice(1).split("");

  let carry = true;
  for (let i = digs.length - 1; carry && i >= 0; i -= 1) {
    // Read from `x`, not `digs`: each index is read once, before it is
    // written, so the two are identical here, and charAt cannot be undefined.
    const d = digits.indexOf(x.charAt(i + 1)) + 1;
    if (d === digits.length) {
      digs[i] = digits.charAt(0);
    } else {
      digs[i] = digits.charAt(d);
      carry = false;
    }
  }

  if (!carry) return head + digs.join("");

  // Overflowed this magnitude; move up to the next one.
  if (head === "Z") return "a" + digits.charAt(0);
  if (head === "z") return null;
  const next = String.fromCharCode(head.charCodeAt(0) + 1);
  if (next > "a") digs.push(digits.charAt(0));
  else digs.pop();
  return next + digs.join("");
}

function decrementInteger(x: string, digits: string): string | null {
  validateInteger(x);
  const head = x.charAt(0);
  const digs = x.slice(1).split("");
  const last = digits.slice(-1);

  let borrow = true;
  for (let i = digs.length - 1; borrow && i >= 0; i -= 1) {
    const d = digits.indexOf(x.charAt(i + 1)) - 1;
    if (d === -1) {
      digs[i] = last;
    } else {
      digs[i] = digits.charAt(d);
      borrow = false;
    }
  }

  if (!borrow) return head + digs.join("");

  if (head === "a") return "Z" + last;
  if (head === "A") return null;
  const next = String.fromCharCode(head.charCodeAt(0) - 1);
  if (next < "Z") digs.push(last);
  else digs.pop();
  return next + digs.join("");
}

/**
 * A key that sorts strictly between `a` and `b`.
 *
 * Pass null for either side to mean "nothing there":
 *
 *   generateKeyBetween(null, null)    first card in an empty column
 *   generateKeyBetween(last, null)    append to the end
 *   generateKeyBetween(null, first)   prepend to the start
 *   generateKeyBetween(above, below)  drop between two cards
 */
export function generateKeyBetween(
  a: string | null,
  b: string | null,
  digits: string = BASE_62_DIGITS,
): string {
  if (a !== null) validateOrderKey(a, digits);
  if (b !== null) validateOrderKey(b, digits);
  if (a !== null && b !== null && a >= b) throw new Error(`${a} >= ${b}`);

  if (a === null) {
    if (b === null) return FIRST_INDEX;

    const ib = getIntegerPart(b);
    const fb = b.slice(ib.length);
    if (ib === "A" + digits.charAt(0).repeat(26)) {
      return ib + midpoint("", fb, digits);
    }
    if (ib < b) return ib;
    const decremented = decrementInteger(ib, digits);
    if (decremented === null) throw new Error("cannot decrement any more");
    return decremented;
  }

  if (b === null) {
    const ia = getIntegerPart(a);
    const fa = a.slice(ia.length);
    const incremented = incrementInteger(ia, digits);
    return incremented ?? ia + midpoint(fa, null, digits);
  }

  const ia = getIntegerPart(a);
  const fa = a.slice(ia.length);
  const ib = getIntegerPart(b);
  const fb = b.slice(ib.length);
  if (ia === ib) return ia + midpoint(fa, fb, digits);

  const incremented = incrementInteger(ia, digits);
  if (incremented === null) throw new Error("cannot increment any more");
  if (incremented < b) return incremented;
  return ia + midpoint(fa, null, digits);
}

/**
 * `n` keys in order, all strictly between `a` and `b`. Used when seeding a
 * board and when importing from Linear, where a whole ordered batch arrives at
 * once and generating them one at a time would make the keys needlessly long.
 */
export function generateNKeysBetween(
  a: string | null,
  b: string | null,
  n: number,
  digits: string = BASE_62_DIGITS,
): string[] {
  if (n <= 0) return [];
  if (n === 1) return [generateKeyBetween(a, b, digits)];

  if (b === null) {
    let c = generateKeyBetween(a, b, digits);
    const result = [c];
    for (let i = 0; i < n - 1; i += 1) {
      c = generateKeyBetween(c, b, digits);
      result.push(c);
    }
    return result;
  }

  if (a === null) {
    let c = generateKeyBetween(a, b, digits);
    const result = [c];
    for (let i = 0; i < n - 1; i += 1) {
      c = generateKeyBetween(a, c, digits);
      result.push(c);
    }
    return result.reverse();
  }

  // Split the range and recurse, so keys stay short rather than growing by one
  // character for every element of a large batch.
  const mid = Math.floor(n / 2);
  const c = generateKeyBetween(a, b, digits);
  return [
    ...generateNKeysBetween(a, c, mid, digits),
    c,
    ...generateNKeysBetween(c, b, n - mid - 1, digits),
  ];
}
